import { useState } from "react";
import { Trash2, AlertTriangle, Loader2, X, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const fmt = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });

function DeletionModal({ isResponsable, graceDays, onClose, onDone }) {
    const [password, setPassword] = useState("");
    const [confirmation, setConfirmation] = useState("");
    const [loading, setLoading] = useState(false);
    const valid = password && confirmation.trim().toUpperCase() === "SUPPRIMER";

    const submit = async (e) => {
        e.preventDefault();
        if (!valid) return;
        setLoading(true);
        try {
            const { data } = await api.post("/account/deletion-request", { password, confirmation });
            toast.success(`Demande enregistrée — suppression prévue le ${fmt(data.deletion_scheduled_for)}.`);
            onDone();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-4" data-testid="danger-zone-modal">
            <form onSubmit={submit} className="bg-paper max-w-lg w-full p-6 border border-rule space-y-4">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="label-eyebrow text-destructive">Confirmation requise</div>
                        <h2 className="h-display text-xl font-semibold mt-1">Demander la suppression</h2>
                    </div>
                    <button type="button" onClick={onClose} className="p-1.5 hover:bg-ink/5" aria-label="Fermer" data-testid="danger-zone-close">
                        <X size={16} />
                    </button>
                </div>
                <div className="p-3 bg-destructive/10 border border-destructive/30 text-xs text-ink/80">
                    La suppression sera exécutée dans <strong>{graceDays} jours</strong>. Vous pouvez l'annuler à tout moment d'ici là.
                    {isResponsable && (
                        <div className="mt-2 pt-2 border-t border-destructive/30">
                            <strong className="text-destructive">Fermeture d'établissement :</strong> tous les comptes formateurs et alternants, bilans, cours, agendas et bulletins seront supprimés. Seules les informations de facturation sont conservées (obligation comptable).
                        </div>
                    )}
                </div>
                <div>
                    <label className="label-eyebrow mb-2 block">Votre mot de passe *</label>
                    <input type="password" className="field-input" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" data-testid="danger-zone-password-input" />
                </div>
                <div>
                    <label className="label-eyebrow mb-2 block">
                        Tapez <code className="px-1.5 py-0.5 bg-ink text-white text-xs">SUPPRIMER</code> pour confirmer *
                    </label>
                    <input type="text" className="field-input font-mono" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} placeholder="SUPPRIMER" autoComplete="off" data-testid="danger-zone-confirm-input" />
                </div>
                <div className="flex items-center justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-rule hover:bg-ink/5" disabled={loading}>Annuler</button>
                    <button type="submit" disabled={loading || !valid} className="inline-flex items-center gap-2 px-4 py-2 bg-destructive text-white text-sm font-medium hover:bg-destructive/90 disabled:opacity-50" data-testid="danger-zone-confirm-button">
                        {loading ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        {loading ? "Envoi…" : "Confirmer la demande"}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default function DangerZone({ privacy, onChanged }) {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [cancelling, setCancelling] = useState(false);

    if (!user) return null;
    const isResponsable = user.role === "responsable";
    const isAdmin = user.role === "admin";
    const scheduled = privacy?.deletion_scheduled_for;
    const graceDays = privacy?.grace_days || 30;

    const cancel = async () => {
        setCancelling(true);
        try {
            await api.delete("/account/deletion-request");
            toast.success("Demande de suppression annulée.");
            onChanged();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setCancelling(false);
        }
    };

    return (
        <section className="mt-6 border border-destructive/30 bg-destructive/5 p-6" data-testid="danger-zone">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="label-eyebrow text-destructive">Article 17 RGPD — Droit à l'effacement</div>
                    <h3 className="h-display text-lg font-semibold mt-1.5 flex items-center gap-2">
                        <AlertTriangle size={18} className="text-destructive" />
                        {isResponsable ? "Fermer l'établissement et supprimer mon compte" : "Supprimer mon compte"}
                    </h3>
                    {scheduled ? (
                        <p className="text-sm text-destructive mt-2 font-medium" data-testid="danger-zone-scheduled">
                            Suppression programmée le {fmt(scheduled)}.
                        </p>
                    ) : (
                        <p className="text-sm text-ink/70 mt-2 max-w-2xl">
                            Après confirmation, un délai de {graceDays} jours s'applique pendant lequel vous pouvez changer d'avis. Passé ce délai, vos données personnelles sont effacées ou anonymisées.
                        </p>
                    )}
                </div>
                {isAdmin && <span className="text-xs font-mono text-muted2 italic">Le compte admin ne peut pas être supprimé via l'interface.</span>}
                {!isAdmin && scheduled && (
                    <button type="button" onClick={cancel} disabled={cancelling} className="btn-outline !text-xs" data-testid="danger-zone-cancel-request">
                        {cancelling ? <Loader2 size={13} className="animate-spin" /> : <Undo2 size={13} />} Annuler la demande
                    </button>
                )}
                {!isAdmin && !scheduled && (
                    <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-destructive text-white text-sm font-medium hover:bg-destructive/90 transition-colors" data-testid="danger-zone-open-button">
                        <Trash2 size={14} /> Demander la suppression
                    </button>
                )}
            </div>
            {open && (
                <DeletionModal
                    isResponsable={isResponsable}
                    graceDays={graceDays}
                    onClose={() => setOpen(false)}
                    onDone={() => { setOpen(false); onChanged(); }}
                />
            )}
        </section>
    );
}
